import React from 'react';
import TableContainer from '@material-ui/core/TableContainer';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { calcOrderProductsSubtotal, calcProductTotal } from '../helpers/utils';
import { IOrderProduct } from '../types/orderProduct';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import { IPartners } from '../types';
import { useOnlineSalesConfig } from '../hooks';
import { ShopMode } from '../redux/types';
import { PriceDisplay } from './PriceDisplay';

const useStyles = makeStyles((theme) => ({
  table: {
    '& .MuiTableCell-root': {
      [theme.breakpoints.down('xs')]: {
        padding: 0,
      },
    },
  },
  tableNameCell: {
    display: 'flex',
    alignItems: 'center',
    '& > .MuiAvatar-root': {
      margin: theme.spacing(1),
    },
  },
}));

interface OrderSummaryProps {
  orderProducts: IOrderProduct[];
  isUserLogged?: boolean;
  editable?: boolean;
  partner?: IPartners;
  removeProductAction?: (productOrderId: number) => void;
  shopMode: ShopMode;
}
export const OrderSummary: React.FC<OrderSummaryProps> = ({
  orderProducts,
  isUserLogged = true,
  editable = false,
  partner,
  removeProductAction,
  shopMode,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const mediaQueryMatchesXs = useMediaQuery(theme.breakpoints.down('xs'));
  const { config } = useOnlineSalesConfig();

  const { subTotal, shippingCosts } = calcOrderProductsSubtotal(orderProducts, !!partner, config, shopMode);

  return (
    <TableContainer component={Paper}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell align="center">Prodotto</TableCell>
            {isUserLogged && <TableCell align="center">Prezzo unitario</TableCell>}
            <TableCell align="center">Quantità</TableCell>
            {isUserLogged && <TableCell align="center">Prezzo</TableCell>}
            {editable && <TableCell padding="none" size="small" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {orderProducts.map((orderProduct) => (
            <TableRow key={orderProduct.id}>
              <TableCell component="th" scope="row" className={classes.tableNameCell}>
                <Avatar
                  src={orderProduct.image ? (orderProduct.image as any).url : 'no-image'}
                  alt={orderProduct.name}
                />
                {orderProduct.name}
              </TableCell>
              {isUserLogged && (
                <TableCell align="right">
                  <Box display="flex" alignItems="center" justifyContent="flex-end">
                    <PriceDisplay
                      price={orderProduct.price}
                      discountedPrice={orderProduct.discountedPrice}
                      shopMode={shopMode}
                      variant="small"
                      showBadge={true}
                    />
                    <Typography variant="body2" style={{ marginLeft: 4 }}>
                      / {orderProduct.unit}
                    </Typography>
                  </Box>
                </TableCell>
              )}
              <TableCell align="right">{`${orderProduct.quantity} ${orderProduct.quantityUnit}`}</TableCell>
              {isUserLogged && (
                <TableCell align="right">{`€ ${calcProductTotal(orderProduct, shopMode).toFixed(2)}`}</TableCell>
              )}
              {editable && (
                <TableCell align="center" padding="none" size="small">
                  <IconButton onClick={() => removeProductAction && removeProductAction(orderProduct.id)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
          {isUserLogged && (
            <>
              <TableRow>
                <TableCell rowSpan={4} colSpan={mediaQueryMatchesXs ? 1 : 2} />
                <TableCell colSpan={mediaQueryMatchesXs ? 2 : 1}>Subtotale</TableCell>
                <TableCell align="right">€ {subTotal.toFixed(2)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={mediaQueryMatchesXs ? 2 : 1}>Spese di consegna</TableCell>
                <TableCell align="right"> {shippingCosts ? `€ ${shippingCosts.toFixed(2)}` : 'Gratuita'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={mediaQueryMatchesXs ? 2 : 1}>Totale</TableCell>
                <TableCell align="right">€ {(subTotal + shippingCosts).toFixed(2)}</TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
